import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Icon, Popover, Spinner } from '@/shared/ui';

import styles from './DetailsPopover.module.scss';

type DetailsPopoverProps = {
  isReportGenerationCompleted: boolean;
  isLoadingPDF: boolean;
  canArchive?: boolean;
  canDelete: boolean;
  canRestore?: boolean;
  hideMode?: boolean;
  isHidden?: boolean;
  shouldHideDownload?: boolean;
  className?: string;
  testID?: string;
  onDetailsView: () => void;
  onRemoveReport: () => void;
  onDownloadReport: () => Promise<void>;
  onHideReport?: () => void;
  onRestoreReport?: () => void;
};

export const DetailsPopover: FC<DetailsPopoverProps> = (props) => {
  const {
    isReportGenerationCompleted,
    isLoadingPDF,
    className,
    testID,
    canArchive,
    canDelete,
    canRestore,
    hideMode,
    isHidden,
    shouldHideDownload,
    onDetailsView,
    onRemoveReport,
    onDownloadReport,
    onHideReport,
    onRestoreReport,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleDetailsView = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDetailsView();
    setIsOpen(false);
  };

  const handleDownloadPDF = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isLoadingPDF) {
      await onDownloadReport();
      setIsOpen(false);
    }
  };

  const handleRemoveReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemoveReport();
    setIsOpen(false);
  };

  const handleHideReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onHideReport?.();
    setIsOpen(false);
  };

  const handleRestoreReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRestoreReport?.();
    setIsOpen(false);
  };

  const removeReportButton = (
    <Button
      onClick={handleRemoveReport}
      variant="tertiary"
      size="small"
      danger
      icon={'delete'}
      className={styles.popoverButton}
    >
      <FormattedMessage id="reportCard.remove" defaultMessage="Remove" />
    </Button>
  );

  if (isHidden) {
    if (!canRestore && !canDelete) {
      return null;
    }

    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className={className}
        testID={testID}
        popoverClassName={styles.popover}
        iconClassName={styles.detailsIcon}
      >
        {canRestore && (
          <Button
            onClick={handleRestoreReport}
            variant="tertiary"
            size="small"
            icon={'back'}
            className={styles.popoverButton}
          >
            <FormattedMessage
              id="reportCard.restore"
              defaultMessage="Restore"
            />
          </Button>
        )}

        {canDelete && removeReportButton}
      </Popover>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      testID={testID}
      popoverClassName={styles.popover}
      iconClassName={styles.detailsIcon}
    >
      <Button
        onClick={handleDetailsView}
        variant="tertiary"
        size="small"
        icon={'info'}
        className={styles.popoverButton}
      >
        <FormattedMessage
          id="reportCard.information"
          defaultMessage="Information"
        />
      </Button>

      {isReportGenerationCompleted && !shouldHideDownload && (
        <Button
          onClick={handleDownloadPDF}
          variant="tertiary"
          size="small"
          className={styles.popoverButton}
        >
          {isLoadingPDF ? (
            <Spinner size={18} className={styles.downloadSpinner} />
          ) : (
            <Icon name="download" size={24} />
          )}
          <FormattedMessage
            id="reportCard.download"
            defaultMessage="Download"
          />
        </Button>
      )}

      {hideMode
        ? canArchive && (
            <Button
              onClick={handleHideReport}
              variant="tertiary"
              size="small"
              icon="archive"
              danger
              className={styles.popoverButton}
            >
              <FormattedMessage id="global.archive" defaultMessage="Archive" />
            </Button>
          )
        : canDelete && removeReportButton}
    </Popover>
  );
};
